<template>
  <div
    class="flex flex-col border-l border-[#E5E5E5] bg-white"
    :class="componentClasses"
  >
    <div class="w-full p-4">
      <div class="flex justify-between">
        <template v-if="!platformStore.isSidebarCollapsed">
          <div
            v-if="item"
            class="font-semibold font-primary text-[18px] leading-[initial] text-gray-800 line-clamp-4"
          >
            {{ itemName }}
          </div>
          <Skeleton v-else class="w-[148px] h-4" />
        </template>
        <Icon
          :size="18"
          name="Upload"
          class="stroke-gray-800 cursor-pointer"
          :class="collapseIconClasses"
          @click="platformStore.toggleSidebarCollapse"
        />
      </div>
      <UiButton
        v-if="showShareButton"
        class="mt-[18px]"
        icon="Share"
        :disabled="isItemInactive"
        @click="handleShareClick"
      >
        Share
      </UiButton>
    </div>
    <Accordion v-if="!platformStore.isSidebarCollapsed" :key="id">
      <AccordionItem id="information">
        <AccordionHeader>
          <template v-if="item">
            <Icon :size="16" name="Info" class="stroke-gray-800 mr-2" />
            <div
              class="font-semibold font-primary text-[14px] leading-[20px] text-gray-800 tracking-[-0.18px]"
            >
              Information
            </div>
          </template>
          <template v-else>
            <Skeleton class="w-[97px] h-[13px]" />
          </template>
        </AccordionHeader>
        <AccordionContent class="overflow-y-auto pb-3 h-full">
          <template v-if="item">
            <SidebarFileInfo
              :item="item"
              :selected-items="selectedItems"
              :is-file-item="isFileItem"
              :is-item-inactive="isItemInactive"
              @preview-click="handlePreviewClick"
              @location-update="handleLocationUpdate"
            />
            <SidebarOwnership
              :item="item"
              :is-file-item="isFileItem"
              :multiple-items-selected="multipleItemsSelected"
            />
            <SidebarAccess
              :item="item"
              :is-file-item="isFileItem"
              :multiple-items-selected="multipleItemsSelected"
            />
          </template>
          <template v-else>
            <Skeleton class="w-[298px] h-[168px] rounded-md" />
          </template>
        </AccordionContent>
      </AccordionItem>
      <SidebarTransactions
        v-if="isFileItem"
        :item="item"
        :is-file-item="isFileItem"
        :multiple-items-selected="multipleItemsSelected"
      />
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
} from '@/shared/components/Accordion'
import { Icon, Skeleton, UiButton, Table } from '@dacr/ui'
import {
  CopyrightStatus,
  type File,
  type Item,
} from '@/shared/graphql/router/graphql'
import { useExplorerStore, type CurrentFolder } from '@/features/explorer/store'
import { truncateMiddle } from '@/shared/utils/string'
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useDevice } from '@/shared/composables/useDevice'
import { usePlatformStore } from '@/shared/stores/platform'
import { TableTypes } from '@/shared/types'
import SidebarFileInfo from './SidebarFileInfo.vue'
import SidebarOwnership from './SidebarOwnership.vue'
import SidebarAccess from './SidebarAccess.vue'
import SidebarTransactions from './SidebarTransactions.vue'

export type SidebarItem = Item | CurrentFolder

const table = Table.useTable(TableTypes.Explorer)

const device = useDevice()
const router = useRouter()
const explorerStore = useExplorerStore()
const platformStore = usePlatformStore()
const { open } = useDialog()

if (device.isTabletHorizontal.value) {
  platformStore.setSidebarCollapse(true)
}

const selectedItems = computed(() =>
  (explorerStore.currentFolder?.items || []).filter((item) =>
    table.isRowSelected(item.id)
  )
)

watch(selectedItems, () => {
  if (document.body.style.pointerEvents === 'none' || device.isDesktop.value)
    return

  platformStore.setSidebarCollapse(!selectedItems.value.length)
})

const item = computed<SidebarItem | null>(() =>
  selectedItems.value.length
    ? selectedItems.value[0]
    : explorerStore.currentFolder
)

const multipleItemsSelected = computed(() => selectedItems.value.length > 1)

const id = computed(() =>
  multipleItemsSelected.value
    ? selectedItems.value.map((row) => row.id).join('-')
    : item.value?.id
)

const isFileItem = computed(() => {
  if (!item.value) return false
  return '__typename' in item.value && item.value.__typename === 'File'
})

const fileItem = computed(() =>
  isFileItem.value ? (item.value as File & { __typename: 'File' }) : null
)

const isItemInactive = computed(
  () => fileItem.value?.copyright?.status === CopyrightStatus.Inactive
)

const itemName = computed(() => {
  if (multipleItemsSelected.value) {
    return `${selectedItems.value.length} items selected`
  }

  if (isFileItem.value) {
    return truncateMiddle(item.value?.name || '', 25)
  }

  return item.value?.name === '/' ? 'Dacr' : item.value?.name
})

const showShareButton = computed(
  () =>
    isFileItem.value &&
    !platformStore.isSidebarCollapsed &&
    !multipleItemsSelected.value
)

const componentClasses = computed(() => ({
  ...(platformStore.isSidebarCollapsed && {
    'min-w-[50px]': true,
    'max-w-[50px]': true,
  }),
  ...(!platformStore.isSidebarCollapsed && {
    'min-w-[330px]': true,
    'max-w-[330px]': true,
    'tablet-h:[box-shadow:0_0_20px_rgba(0,0,0,0.1)]':
      device.isTabletHorizontal.value,
    'tablet-h:[clip-path:inset(0_0_0_-20px)]': device.isTabletHorizontal.value,
  }),
}))

const collapseIconClasses = computed(() => ({
  ...(platformStore.isSidebarCollapsed && { '-rotate-90': true }),
  ...(!platformStore.isSidebarCollapsed && { 'rotate-90': true }),
}))

const handleLocationUpdate = (path: string[]) => {
  if (path.length === 1) {
    router.push({ name: 'FileExplorerRootFolderView' })
  } else {
    router.push({
      name: 'FileExplorerFolderView',
      params: { path: path.slice(1) },
    })
  }
}

const handlePreviewClick = () => {
  if (!isFileItem.value || isItemInactive.value) return
  router.push(`/file/${fileItem.value?.copyright?.id}`)
}

const handleShareClick = () => {
  open(Dialogs.Share, {
    props: { file: fileItem.value },
  })
}
</script>
